// src/pages/finance-portal/reports/IndustryBenchmarks.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Stack, Chip,
} from '@mui/material';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatNumber,
    firstOfYearIso, todayIso,
} from './reportShared';

export default function IndustryBenchmarks() {
    const [industryCode, setIndustryCode] = useState('');
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [benchmarks, setBenchmarks] = useState([]);
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [dialog, setDialog] = useState({ open: false });
    const [form, setForm] = useState({
        industry_code: '', industry_name: '', metric_key: '', metric_name: '',
        avg_value: 0, percentile_25: 0, percentile_50: 0, percentile_75: 0,
        unit: 'ratio',
    });

    const fetchBenchmarks = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/industry-benchmarks', {
                params: { industry_code: industryCode || undefined },
            });
            if (res.data.success) setBenchmarks(res.data.data || []);
        } catch (e) { /* ignore */ }
    }, [industryCode]);

    const loadComparison = useCallback(async () => {
        if (!industryCode) { setComparison(null); return; }
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/analytics/industry-comparison', {
                params: { industry_code: industryCode, start_date: start, end_date: end },
            });
            if (res.data.success) setComparison(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load comparison');
        } finally { setLoading(false); }
    }, [industryCode, start, end]);

    useEffect(() => { fetchBenchmarks(); }, [fetchBenchmarks]);
    useEffect(() => { loadComparison(); }, [loadComparison]);

    const addBenchmark = async () => {
        try {
            await axiosInstance.post('/api/finance/industry-benchmarks', form);
            setDialog({ open: false });
            fetchBenchmarks();
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to add');
        }
    };

    const pctColor = (rank) => {
        if (!rank) return 'default';
        if (rank.includes('Below')) return 'error';
        if (rank.includes('Above')) return 'success';
        return 'primary';
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <EqualizerIcon sx={{ color: ACCENT }} />
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Industry Benchmarks</Typography>
                </Stack>
                <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => setDialog({ open: true })}>Add Benchmark</Button>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField label="Industry Code" fullWidth size="small"
                            value={industryCode} onChange={(e) => setIndustryCode(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField label="Start" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={loadComparison} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}
                            disabled={!industryCode}>Compare</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {comparison && !loading && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {comparison.industry_name} ({comparison.industry_code})
                    </Typography>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Metric</TableCell>
                                    <TableCell align="right">Your Value</TableCell>
                                    <TableCell align="right">Industry Avg</TableCell>
                                    <TableCell align="right">Gap</TableCell>
                                    <TableCell>Percentile Rank</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {comparison.rows.map((r) => (
                                    <TableRow key={r.metric_key}>
                                        <TableCell>{r.metric_name}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatNumber(r.your_value, 4)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.avg_value !== null ? formatNumber(r.avg_value, 4) : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.gap_vs_avg !== null ? formatNumber(r.gap_vs_avg, 4) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {r.percentile_rank && (
                                                <Chip size="small" label={r.percentile_rank}
                                                    color={pctColor(r.percentile_rank)} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Available Benchmarks
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Industry</TableCell>
                                <TableCell>Metric</TableCell>
                                <TableCell align="right">Avg</TableCell>
                                <TableCell align="right">P25 / P50 / P75</TableCell>
                                <TableCell>Source</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {benchmarks.map((b) => (
                                <TableRow key={b.id}>
                                    <TableCell>{b.industry_code} · {b.industry_name}</TableCell>
                                    <TableCell>{b.metric_name}</TableCell>
                                    <TableCell align="right">
                                        {b.avg_value !== null ? formatNumber(b.avg_value, 4) : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {[b.percentile_25, b.percentile_50, b.percentile_75]
                                            .map((v) => v !== null ? formatNumber(v, 2) : '—').join(' / ')}
                                    </TableCell>
                                    <TableCell>{b.source || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="sm" fullWidth>
                <DialogTitle>Add Industry Benchmark</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={6}>
                            <TextField label="Industry Code" fullWidth size="small"
                                value={form.industry_code}
                                onChange={(e) => setForm({ ...form, industry_code: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Industry Name" fullWidth size="small"
                                value={form.industry_name}
                                onChange={(e) => setForm({ ...form, industry_name: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Metric Key" fullWidth size="small"
                                value={form.metric_key}
                                onChange={(e) => setForm({ ...form, metric_key: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Metric Name" fullWidth size="small"
                                value={form.metric_name}
                                onChange={(e) => setForm({ ...form, metric_name: e.target.value })} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField label="Avg" type="number" fullWidth size="small"
                                value={form.avg_value}
                                onChange={(e) => setForm({ ...form, avg_value: parseFloat(e.target.value) })} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField label="P25" type="number" fullWidth size="small"
                                value={form.percentile_25}
                                onChange={(e) => setForm({ ...form, percentile_25: parseFloat(e.target.value) })} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField label="P50" type="number" fullWidth size="small"
                                value={form.percentile_50}
                                onChange={(e) => setForm({ ...form, percentile_50: parseFloat(e.target.value) })} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField label="P75" type="number" fullWidth size="small"
                                value={form.percentile_75}
                                onChange={(e) => setForm({ ...form, percentile_75: parseFloat(e.target.value) })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ open: false })}>Cancel</Button>
                    <Button variant="contained" onClick={addBenchmark}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
